<script setup lang="ts">
import { computed, onMounted, ref, toValue, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useRoute, useRouter } from "vue-router";
import {
  UiDialog,
  UiFormField,
  UiIcon,
  UiInputField,
  UiMultiSelectChips,
  UiStandardButton,
  UiText,
  UiTextarea,
  useApi,
  useToaster,
} from "@shopexperts/shared";
import type { AdminPage, CreatePageCollectionRequest, PageCollection, UpdatePageCollectionRequest } from "@shopexperts/shared";
import { useCreatePageCollection } from "../composables/useCreatePageCollection";
import { useDeletePageCollection } from "../composables/useDeletePageCollection";
import { usePageCollection } from "../composables/usePageCollection";
import { usePageTags } from "../composables/usePageTags";
import { useUpdatePageCollection } from "../composables/useUpdatePageCollection";

const api = useApi();
const route = useRoute();
const router = useRouter();
const toaster = useToaster();
const createCollection = useCreatePageCollection();
const updateCollection = useUpdatePageCollection();
const deleteCollection = useDeletePageCollection();
const { tags: availableTags } = usePageTags();

const collectionId = computed(() => Number(route.params.collectionId || 0));
const isEditMode = computed(() => collectionId.value > 0);
const { collection, isLoading: isLoadingCollection, error: collectionError } = usePageCollection(collectionId);

const title = ref("");
const description = ref("");
const slug = ref("");
const slugTouched = ref(false);
const tags = ref<string[]>([]);
const tagDraft = ref("");
const seoTitle = ref("");
const seoDescription = ref("");
const deleteDialogOpen = ref(false);
const hydratedCollectionId = ref<number | null>(null);

const slugModel = computed({
  get: () => slug.value,
  set: (value: string) => {
    slugTouched.value = true;
    slug.value = slugify(value);
  },
});

const tagOptions = computed(() =>
  Array.from(new Set([...availableTags.value, ...tags.value]))
    .sort((a, b) => a.localeCompare(b))
    .map((tag) => ({ value: tag, label: tag })),
);

const matchingPagesQuery = useQuery({
  queryKey: computed(() => [
    "admin",
    "pages",
    "collection-preview",
    {
      tags: [...tags.value].sort(),
    },
  ]),
  queryFn: () => api.admin.pages.listPages({
    query: {
      tags: tags.value,
      sort: "updated_at",
      direction: "desc",
      limit: 50,
    },
  }),
  enabled: computed(() => tags.value.length > 0),
  staleTime: 60 * 1000,
});

const matchingPages = computed(() => matchingPagesQuery.data.value?.data || []);
const matchedPagesCount = computed(() => matchingPagesQuery.data.value?.meta?.total ?? collection.value?.pages_count ?? matchingPages.value.length);
const publishedPagesCount = computed(() => matchingPages.value.length
  ? matchingPages.value.filter((page) => page.status === "published").length
  : collection.value?.published_pages_count ?? 0,
);
const draftPagesCount = computed(() => matchingPages.value.length
  ? matchingPages.value.filter((page) => page.status === "draft").length
  : collection.value?.draft_pages_count ?? 0,
);
const pagePreviewHeading = computed(() => isEditMode.value ? "Pages in this collection" : "Matching pages preview");
const pagePreviewDescription = computed(() => isEditMode.value
  ? "Pages currently matching this collection's tags."
  : "These pages match the selected tags and will be included in this collection."
);
const pageTitleText = computed(() => isEditMode.value ? title.value || "Collection Editor" : "Create Collection");
const pageTitleMarkup = computed(() => `**${pageTitleText.value}**`);
const isSaving = computed(() => Boolean(toValue(createCollection.isPending) || toValue(updateCollection.isPending)));
const isDeleting = computed(() => Boolean(toValue(deleteCollection.isPending)));
const hasRequiredFields = computed(() => title.value.trim().length > 0 && slug.value.trim().length > 0 && tags.value.length > 0);
const pagesTableColumns = "minmax(220px,1.3fr) minmax(220px,1.25fr) minmax(180px,1fr) minmax(110px,0.65fr) minmax(160px,0.9fr)";

onMounted(() => {
  window.scrollTo({ top: 0 });
});

watch(title, (value) => {
  if (!slugTouched.value) slug.value = slugify(value);
});

watch(collection, (value) => {
  if (!value || hydratedCollectionId.value === value.id) return;
  hydrateCollection(value);
  hydratedCollectionId.value = value.id;
});

function hydrateCollection(value: PageCollection) {
  title.value = value.title;
  description.value = value.description || "";
  slug.value = value.slug;
  slugTouched.value = true;
  tags.value = [...value.tags];
  seoTitle.value = value.seo_title || "";
  seoDescription.value = value.seo_description || "";
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/^\/+/, "")
    .replace(/^collections\//, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildPayload(): CreatePageCollectionRequest | UpdatePageCollectionRequest {
  return {
    title: title.value.trim(),
    description: description.value.trim() || null,
    slug: slug.value.trim(),
    tags: tags.value,
    seo_title: seoTitle.value.trim() || null,
    seo_description: seoDescription.value.trim() || null,
  };
}

async function saveCollection() {
  if (!hasRequiredFields.value) {
    toaster.create({
      title: "Collection needs a title, slug, and tags",
      type: "error",
    });
    return;
  }

  if (isEditMode.value) {
    await updateCollection.mutateAsync({
      id: collectionId.value,
      data: buildPayload(),
    });
    return;
  }

  const created = await createCollection.mutateAsync(buildPayload() as CreatePageCollectionRequest);
  void router.push({ name: "admin-page-collections-edit", params: { collectionId: created.id } });
}

async function confirmDelete() {
  if (!isEditMode.value) return;
  await deleteCollection.mutateAsync(collectionId.value);
  deleteDialogOpen.value = false;
  void router.push({ name: "admin-page-collections" });
}

function cancel() {
  void router.push({ name: "admin-page-collections" });
}

function addDraftTag() {
  const parts = tagDraft.value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return;
  tags.value = Array.from(new Set([...tags.value, ...parts]));
  tagDraft.value = "";
}

function pageTitle(page: AdminPage) {
  const value = page.metafields?.title;
  return typeof value === "string" && value.trim() ? value : page.path;
}

function pageTags(page: AdminPage) {
  return page.tags || [];
}

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}
</script>

<template>
  <main class="min-w-0 flex-1 bg-white px-6 py-8 md:px-10 lg:px-16">
    <div class="mx-auto flex w-full max-w-[1500px] flex-col gap-8">
      <header class="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
        <div class="min-w-0">
          <nav class="mb-5 flex items-center gap-2 text-sm text-se-gray-700">
            <button type="button" class="text-blue-500 hover:text-se-dark" @click="cancel">
              Page Collections
            </button>
            <span>/</span>
            <span class="font-semibold text-se-dark">{{ isEditMode ? collection?.title || 'Collection' : 'New Collection' }}</span>
          </nav>
          <UiText
            as="h1"
            variant="h1"
            class="page-collection-editor-title"
            :text="pageTitleMarkup"
          />
          <UiText
            as="p"
            variant="p"
            class="mt-3 max-w-4xl text-se-gray-700"
            :text="isEditMode ? description || 'Update the details and tags for this collection.' : 'Define collection metadata and choose tags to automatically group matching pages.'"
          />
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <UiStandardButton label="Cancel" variant="outline" @click="cancel" />
          <UiStandardButton :label="isEditMode ? 'Save Changes' : 'Create Collection'" class="page-collection-primary-button" :disabled="isSaving" @click="saveCollection" />
        </div>
      </header>

      <div v-if="isLoadingCollection && isEditMode" class="rounded-lg border border-se-gray-300 p-10 text-center text-se-gray-700">
        Loading collection...
      </div>
      <div v-else-if="collectionError && isEditMode" class="rounded-lg border border-red-200 p-10 text-center text-red-600">
        Failed to load collection.
      </div>

      <div v-else class="page-collection-editor-grid">
        <section class="flex min-w-0 flex-col gap-5">
          <div v-if="isEditMode" class="page-collection-panel rounded-lg border border-se-gray-300 bg-white p-6">
            <UiText as="h2" variant="h5" class="font-semibold text-se-dark" text="Collection summary" />
            <div class="mt-5 grid gap-5">
              <dl class="page-collection-summary-list text-sm text-se-gray-700">
                <div>
                  <dt class="font-semibold text-se-dark">Title</dt>
                  <dd class="mt-1">{{ title || '-' }}</dd>
                </div>
                <div>
                  <dt class="font-semibold text-se-dark">Tags</dt>
                  <dd class="mt-2 flex flex-wrap gap-2">
                    <span v-for="tag in tags" :key="tag" class="rounded bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-600">{{ tag }}</span>
                    <span v-if="!tags.length">-</span>
                  </dd>
                </div>
                <div>
                  <dt class="font-semibold text-se-dark">Slug</dt>
                  <dd class="mt-1">{{ slug || '-' }}</dd>
                </div>
                <div>
                  <dt class="font-semibold text-se-dark">Description</dt>
                  <dd class="mt-1">{{ description || '-' }}</dd>
                </div>
              </dl>
              <div class="page-collection-metric-grid">
                <div class="page-collection-metric rounded-md border border-se-gray-200 p-4">
                  <span class="text-sm font-semibold text-se-dark">Matched pages</span>
                  <strong class="mt-3 block text-3xl font-normal text-se-dark">{{ matchedPagesCount }}</strong>
                </div>
                <div class="page-collection-metric rounded-md border border-se-gray-200 p-4">
                  <span class="text-sm font-semibold text-se-dark">Published pages</span>
                  <strong class="mt-3 block text-3xl font-normal text-se-dark">{{ publishedPagesCount }}</strong>
                </div>
                <div class="page-collection-metric rounded-md border border-se-gray-200 p-4">
                  <span class="text-sm font-semibold text-se-dark">Draft pages</span>
                  <strong class="mt-3 block text-3xl font-normal text-se-dark">{{ draftPagesCount }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div class="page-collection-panel overflow-x-auto rounded-lg border border-se-gray-300 bg-white">
            <div class="border-b border-se-gray-200 p-6">
              <UiText as="h2" variant="h5" class="font-semibold text-se-dark" :text="pagePreviewHeading" />
              <UiText as="p" variant="caption-normal" class="mt-2 text-se-gray-700" :text="pagePreviewDescription" />
              <UiText as="p" variant="p-small" class="mt-8 text-se-gray-700" :text="`${matchedPagesCount} matching pages`" />
            </div>

            <div class="grid min-w-[920px] gap-5 border-b border-se-gray-200 px-6 py-4 text-xs font-semibold uppercase tracking-[0.08em] text-se-gray-700" :style="{ gridTemplateColumns: pagesTableColumns }">
              <span>Page title</span>
              <span>Path</span>
              <span>Tags</span>
              <span>Status</span>
              <span>Updated</span>
            </div>

            <div
              v-for="pageItem in matchingPages"
              :key="pageItem.id"
              class="grid min-w-[920px] items-center gap-5 border-b border-se-gray-200 px-6 py-4 text-sm last:border-b-0"
              :style="{ gridTemplateColumns: pagesTableColumns }"
            >
              <span class="font-semibold text-se-dark">{{ pageTitle(pageItem) }}</span>
              <span class="text-se-gray-700">{{ pageItem.path }}</span>
              <span class="flex flex-wrap gap-2">
                <span v-for="tag in pageTags(pageItem)" :key="tag" class="rounded bg-se-gray-100 px-2 py-1 text-xs font-semibold text-se-gray-700">{{ tag }}</span>
              </span>
              <span class="inline-flex items-center gap-2 text-se-gray-700">
                <span class="h-2 w-2 rounded-full" :class="pageItem.status === 'published' ? 'bg-emerald-500' : 'bg-amber-500'"></span>
                {{ pageItem.status }}
              </span>
              <span class="text-se-gray-700">
                <span class="block">{{ formatDate(pageItem.updated_at) }}</span>
                <span v-if="pageItem.last_editor" class="block">by {{ pageItem.last_editor.name }}</span>
              </span>
            </div>

            <div v-if="matchingPagesQuery.isLoading.value" class="px-6 py-14 text-center text-se-gray-700">Loading matching pages...</div>
            <div v-else-if="!tags.length" class="px-6 py-14 text-center text-se-gray-700">Add tags to preview matching pages.</div>
            <div v-else-if="matchingPagesQuery.error.value" class="px-6 py-14 text-center text-red-600">Failed to load matching pages.</div>
            <div v-else-if="!matchingPages.length" class="px-6 py-14 text-center text-se-gray-700">No pages match these tags.</div>
          </div>
        </section>

        <aside class="page-collection-panel rounded-lg border border-se-gray-300 bg-white p-6">
          <UiText as="h2" variant="h5" class="font-semibold text-se-dark" :text="isEditMode ? 'Collection Editor' : 'New Collection'" />
          <UiText
            as="p"
            variant="caption-normal"
            class="mt-2 text-se-gray-700"
            :text="isEditMode ? 'Update the details and tags for this collection.' : 'Create metadata and matching rules for a page collection.'"
          />

          <form class="mt-6 flex flex-col gap-5" @submit.prevent="saveCollection">
            <UiFormField name="collection-title" label="Title" :label-capitalize="false">
              <UiInputField v-model="title" placeholder="Expert SEO Resources" />
            </UiFormField>

            <UiFormField name="collection-slug" label="Slug" :label-capitalize="false">
              <UiInputField v-model="slugModel" placeholder="expert-seo-resources" />
              <p class="mt-1 text-xs text-se-gray-700">Used as an internal collection identifier for section settings and API requests.</p>
            </UiFormField>

            <UiFormField name="collection-description" label="Description" :label-capitalize="false">
              <UiTextarea v-model="description" placeholder="Curated guides, pricing resources, and expert insights." :rows="4" />
            </UiFormField>

            <UiFormField name="collection-tags" label="Tags" :label-capitalize="false">
              <UiMultiSelectChips v-model="tags" :options="tagOptions" placeholder="Select tags..." open-on-click show-chevron />
              <div class="mt-2 flex gap-2">
                <UiInputField v-model="tagDraft" placeholder="Add tag or comma list" @keydown.enter.prevent="addDraftTag" />
                <UiStandardButton label="Add" variant="outline" @click="addDraftTag" />
              </div>
              <p class="mt-1 text-xs text-se-gray-700">Pages must contain every selected tag to match this collection.</p>
            </UiFormField>

            <UiFormField name="collection-seo-title" label="SEO title" :label-capitalize="false" :inline-hint="`${seoTitle.length}/70 characters`">
              <UiInputField v-model="seoTitle" placeholder="Shopify SEO Guides | Shopexperts" />
            </UiFormField>

            <UiFormField name="collection-seo-description" label="SEO description" :label-capitalize="false" :inline-hint="`${seoDescription.length}/160 characters`">
              <UiTextarea v-model="seoDescription" placeholder="Expert Shopify SEO guides to improve rankings and grow traffic." :rows="4" />
            </UiFormField>

            <button
              v-if="isEditMode"
              type="button"
              class="mt-2 inline-flex items-center gap-2 self-start text-sm font-semibold text-red-600"
              @click="deleteDialogOpen = true"
            >
              <UiIcon name="delete" class="h-4 w-4" />
              Delete collection
            </button>
          </form>
        </aside>
      </div>
    </div>

    <UiDialog
      v-model="deleteDialogOpen"
      title="Delete collection?"
      text="This removes public access to this collection slug. Matching pages are not deleted."
      primary-action-label="Delete collection"
      secondary-action-label="Cancel"
      :loading="isDeleting"
      @primary-action="confirmDelete"
      @secondary-action="deleteDialogOpen = false"
    />
  </main>
</template>

<style scoped>
.page-collection-editor-grid {
  align-items: start;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr) 430px;
}

.page-collection-editor-title {
  color: #08275a;
  letter-spacing: 0;
}

.page-collection-primary-button {
  background: linear-gradient(180deg, #193255 0%, #0b1b35 100%);
  color: white;
  box-shadow: 0 10px 20px rgb(8 25 57 / 14%);
}

.page-collection-panel {
  border-color: #d9e0ec;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgb(8 25 57 / 4%);
}

.page-collection-summary-list {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.page-collection-metric-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.page-collection-metric {
  border-color: #e0e6f0;
  border-radius: 10px;
}

@media (max-width: 1400px) {
  .page-collection-editor-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .page-collection-summary-list,
  .page-collection-metric-grid {
    grid-template-columns: 1fr;
  }
}
</style>
